// The AI Command Center's navigation model (public/dashboard-develop.html) —
// the same destinations as the classic sidebar (public/sidebar-include.js)
// plus the Menu Lab test surface, expressed as direct-action tools.
//
// Every tool with an `href` navigates immediately on click. Only `admin`
// carries children: it genuinely holds multiple destinations, so it opens a
// compact submenu whose items themselves navigate directly. `aliases` are
// additional pages that belong to a tool's area (used to map Counter
// Intelligence chat links onto the tool for the fly-to highlight).
//
// `icon` is a symbolic name resolved to a lucide-react component in the view
// layer, so this model stays dependency-free and unit-testable.

export const NAV_TOOLS = [
  {
    key: 'counts',
    label: 'Counts',
    icon: 'counts',
    href: '/count.html?view=overview',
    blurb: 'Run and review inventory counts',
    primary: true,
    aliases: ['/count-templates.html', '/count-sessions.html', '/count-session.html'],
  },
  {
    key: 'items',
    label: 'Items',
    icon: 'items',
    href: '/items.html',
    blurb: 'Catalog, prices, and PARs',
    primary: true,
    aliases: ['/add-items.html'],
  },
  {
    key: 'purchasing',
    label: 'Purchasing',
    icon: 'purchasing',
    href: '/ordering.html',
    blurb: 'Orders, vendors, and invoices',
    primary: true,
    aliases: ['/vendor-editor.html', '/invoice-inbox.html', '/orders.html'],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: 'reports',
    href: '/reports.html',
    blurb: 'Usage, cost of goods, and P&L',
    primary: true,
  },
  {
    key: 'recipes',
    label: 'Recipes',
    icon: 'recipes',
    href: '/menu-costing-lab.html',
    blurb: 'Menu Lab — recipe costing (preview)',
    primary: false,
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: 'admin',
    blurb: 'Stores, account, and billing',
    primary: false,
    children: [
      { key: 'switch-store', label: 'Switch Store', icon: 'switch-store', href: '/select-store.html' },
      { key: 'manage-stores', label: 'Manage Stores', icon: 'manage-stores', href: '/store-settings.html' },
      { key: 'account', label: 'Account', icon: 'account', href: '/user-settings.html' },
      { key: 'billing', label: 'Billing', icon: 'billing', href: '/store-settings.html#subscription' },
    ],
  },
  {
    key: 'support',
    label: 'Support',
    icon: 'support',
    href: '/support.html',
    blurb: 'Help, guides, and contact',
    primary: false,
  },
  {
    key: 'classic',
    label: 'Classic View',
    icon: 'classic',
    href: '/dashboard.html',
    blurb: 'The original dashboard',
    primary: false,
    secondary: true,
  },
];

const parseHref = (href) => {
  if (typeof href !== 'string' || !href.trim()) return null;
  try {
    const url = new URL(href, 'http://countertop.local');
    return { pathname: url.pathname, hash: url.hash || '' };
  } catch (_) {
    return null;
  }
};

/**
 * Resolve an app href to a navigation target: { nodeKey, childKey|null, href }.
 * Returns null when the href doesn't belong to any tool (external links,
 * unmapped pages) — callers fall back to plain navigation.
 *
 * Matching is by pathname; a candidate that carries a hash (Billing →
 * /store-settings.html#subscription) also requires that hash, and
 * hash-specific candidates are tried first so #subscription resolves to
 * Billing while /store-settings.html#team falls through to Manage Stores.
 * Tool `aliases` map related pages (e.g. /vendor-editor.html) onto their
 * parent tool.
 */
export function findNavTarget(href, tools = NAV_TOOLS) {
  const target = parseHref(href);
  if (!target) return null;

  const candidates = [];
  tools.forEach((tool) => {
    if (tool.href) candidates.push({ nodeKey: tool.key, childKey: null, href: tool.href });
    (tool.aliases || []).forEach((alias) => {
      candidates.push({ nodeKey: tool.key, childKey: null, href: alias });
    });
    (tool.children || []).forEach((child) => {
      if (child.href) candidates.push({ nodeKey: tool.key, childKey: child.key, href: child.href });
    });
  });
  candidates.sort((a, b) => {
    const aHash = parseHref(a.href)?.hash ? 1 : 0;
    const bHash = parseHref(b.href)?.hash ? 1 : 0;
    return bHash - aHash;
  });

  for (const candidate of candidates) {
    const parsed = parseHref(candidate.href);
    if (!parsed || parsed.pathname !== target.pathname) continue;
    if (parsed.hash && parsed.hash !== target.hash) continue;
    return candidate;
  }
  return null;
}
